import {
    createBaseThemeOptions,
    createUnifiedTheme,
    defaultTypography,
    palettes,
} from '@backstage/theme';

import TietoevrySans1Regular from '../assets/fonts/TietoevrySans1-Regular.woff';
import TietoevrySans1Bold from '../assets/fonts/TietoevrySans1-Bold.woff';
import TietoevrySans1Thin from '../assets/fonts/TietoevrySans1-Thin.woff';


/**
 * Tietoevry Sans 1 custom font definitions
 * Loaded as WOFF format for optimal web performance
 */
const tietoEvryCustomFontRegular = {
    fontFamily: '"Tietoevry_Sans_1"',
    fontStyle: 'normal',
    fontWeight: 400,
    src: `url(${TietoevrySans1Regular}) format('woff')`,
};

const tietoEvryCustomFontBold = {
    fontFamily: '"Tietoevry_Sans_1"',
    fontStyle: 'normal',
    fontWeight: 700,
    src: `url(${TietoevrySans1Bold}) format('woff')`,
};

const tietoEvryCustomFontThin = {
    fontFamily: '"Tietoevry_Sans_1"',
    fontStyle: 'normal',
    fontWeight: 100,
    src: `url(${TietoevrySans1Thin}) format('woff')`,
};
/**
 * Custom theme configuration for the Tietoevry Backstage application
 * Includes font loading, scrollbar preservation, search modal UI fixes, and navbar styling
 */
export const tietoTheme = createUnifiedTheme({
    fontFamily: '"Tietoevry_Sans_1",Helvetica,Arial,sans-serif;',
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                '@font-face': [tietoEvryCustomFontRegular, tietoEvryCustomFontBold, tietoEvryCustomFontThin],

                // ========================================
                // SEARCH MODAL GRID CONTAINER HIDING
                // Hides unwanted MuiGrid-container within search modals
                // ========================================
                '[role="dialog"] .MuiGrid-container, .MuiDialog-root .MuiGrid-container, [data-testid="search-modal"] .MuiGrid-container, .search-modal .MuiGrid-container': {
                    display: 'none !important',
                },

                // ========================================
                // NAVBAR ICON STYLING
                // Ensures all navbar icons are bold white and consistent
                // ========================================
                '.MuiAppBar-root .MuiIconButton-root': {
                    color: '#ffffff !important',
                    '& svg': {
                        color: '#ffffff !important',
                        fill: '#ffffff !important',
                    },
                },

                // ========================================
                // SEARCH BUTTON HOVER EFFECTS REMOVAL
                // Disables all hover/focus/active backgrounds for search button
                // ========================================
                '.MuiAppBar-root .MuiIconButton-root[aria-label="search"]': {
                    '&:hover, &:focus, &:active': {
                        backgroundColor: 'transparent !important',
                    },
                },

                // ========================================
                // BACKSTAGE SIDEBAR ITEM HOVER OVERRIDES
                // Prevents unwanted hover backgrounds in navbar for Backstage components
                // ========================================
                '.MuiAppBar-root [class*="BackstageSidebarItem-highlightable"]:hover, .MuiAppBar-root [class*="SidebarItem"]:hover, .MuiAppBar-root [class*="sidebar"]:hover, .MuiAppBar-root [class*="SidebarSearch"] *:hover': {
                    backgroundColor: 'transparent !important',
                },
            },
        },
        // ========================================
        // MATERIAL-UI COMPONENT OVERRIDES
        // ========================================
        MuiAppBar: {
            styleOverrides: {
                root: {
                    gap: "0.5rem",
                    height: "3rem"
                },
                colorPrimary: {
                    color: "#EDEEEE",
                    backgroundColor: "#071d49",
                    left: "0",
                    position: "sticky",
                    right: "0",
                    top: "0",
                    zIndex: "4",
                    boxShadow: 'none',
                    borderBottom: '2px solid rgba(0, 0, 0, 0.2)',
                }
            },
        },
        MuiButton: {
            styleOverrides: {
                textPrimary: {
                    color: "#071D49bb !important",
                }
            },
        },
        MuiToolbar: {
            styleOverrides: {
                regular: {
                    minHeight: "56px",
                    '@media (min-width: 600px)': {
                        minHeight: "56px"
                    },
                    '@media (min-width: 0px) and (orientation: landscape)': {
                        minHeight: "48px"
                    }
                },
            },
        },

        // ========================================
        // BACKSTAGE COMPONENT OVERRIDES
        // ========================================
        BackstageHeader: {
            styleOverrides: {
                header: ({ theme: _theme }) => ({
                    backgroundColor: "#f8f7f7",
                    backgroundImage: "",
                    boxShadow: "",
                    display: "none",
                }),
                title: ({ theme: _theme }) => ({
                    color: "#280071cc"
                }),
            },
        },
        BackstageItemCardHeader: {
            styleOverrides: {
                root: {
                    color: "#EDEEEE",
                    background: '#071D49bb !important',
                    fontFamily: '"Tietoevry_Sans_1",Helvetica,Arial,sans-serif;',
                },
            },
        },
        BackstageContentHeader: {
            styleOverrides: {
                title: {
                    display: 'none',
                },
            },
        },
       
    },
    ...createBaseThemeOptions({
        palette: palettes.light,

        typography: {
            ...defaultTypography,
            htmlFontSize: 16,
            fontFamily: '"Tietoevry_Sans_1",Helvetica,Arial,sans-serif;',
        },
        defaultPageTheme: 'home',
    }),
});